import { yupResolver } from "@hookform/resolvers/yup";
import { t } from "i18next";
import type React from "react";
import { useEffect, useMemo, useState } from "react";
import type { SubmitHandler } from "react-hook-form";
import { useForm } from "react-hook-form";
import { useQueryClient } from '@tanstack/react-query';
import * as yup from "yup";

import Button from "~/components/buttons/Button/Button.tsx";
import FormHasErrorsHint from "~/components/formElements/FormHasErrorsHint";
import { FormSectionGroup } from "~/components/formElements/FormSection";
import AddressFormSection from "~/components/formSections/AddressFormSection";
import SubmitButton from "~/components/formElements/SubmitButton";
import SidebarBusyOverlay from "~/components/Sidebar/components/SidebarBusyOverlay";
import SidebarContent from "~/components/Sidebar/components/SidebarContent";
import SidebarFooter from "~/components/Sidebar/components/SidebarFooter";
import SidebarHeader from "~/components/Sidebar/components/SidebarHeader";
import SidebarHeaderHeadline from "~/components/Sidebar/components/SidebarHeaderHeadline";
import useClientSelectOptions from "~/hooks/form/formOptionsData/useClientSelectOptions.ts";
import useCountrySelectOptions from "~/hooks/form/formOptionsData/useCountrySelectOptions.ts";
import useGenderSelectOptions from "~/hooks/form/formOptionsData/useGenderSelectOptions.ts";
import useLanguageSelectOptions from "~/hooks/form/formOptionsData/useLanguageSelectOptions.ts";
import useSalesFunnelSelectOptions from "~/hooks/form/formOptionsData/useSalesFunnelSelectOptions.ts";
import useSalesTypeSelectOptions from "~/hooks/form/formOptionsData/useSalesTypeSelectOptions.ts";
import useUserSelectOptions, { employeesFilterFn } from "~/hooks/form/formOptionsData/useUsersSelectOptions.ts";
import { useFormIsSubmittable } from "~/hooks/form/useFormIsSubmittable.ts";
import type { Client } from "~/modules/client/api/client/clientTypes.ts";
import {
	updateClientContactPerson,
} from "~/modules/client/api/clientContactPerson/clientContactPersonApiDispatchers.ts";
import {
	ALL_CLIENT_CONTACT_PERSONS_QUERY_KEY,
	CLIENT_CONTACT_PERSON_QUERY_KEY,
} from "~/modules/client/api/clientContactPerson/clientContactPersonQueries.ts";
import type {
	ClientContactPersonType,
	UpdateClientContactPersonData,
} from "~/modules/client/api/clientContactPerson/clientContactPersonTypes.ts";
import ContactCompanyFormSection
	from "~/modules/client/components/contact/forms/formSections/ContactCompanyFormSection";
import ContactContactFormSection
	from "~/modules/client/components/contact/forms/formSections/ContactContactFormSection";
import ContactPersonalInformationFormSection
	from "~/modules/client/components/contact/forms/formSections/ContactPersonalInformationFormSection";
import ContactSalesFormSection from "~/modules/client/components/contact/forms/formSections/ContactSalesFormSection";
import { normalizeTKey } from "~/types/typeHelpers.ts";
import { formatDateToYYYYMMDD } from "~/utils/dateAndTimeUtils.ts";
import { preventSubmitOnEnter, transformEmptyDateValueToNull } from "~/utils/form/formUtils.ts";

type Props = {
	allClientsData: Client[];
	onSuccess: () => void;
	onCancel: () => void;
	contactPersonData: ClientContactPersonType;
};

interface UpdateClientContactPersonFormData extends Omit<UpdateClientContactPersonData, "newClientId" | "dateOfBirth"> {
	dateOfBirth: Date | null;
}

const userFilterOptions = {filterFunction:employeesFilterFn}

const UpdateClientContactPersonForm: React.FC<Props> = ({ allClientsData, onSuccess, onCancel, contactPersonData }) => {
	//const { t } = useTranslation();
	const [busy, setBusy] = useState(false);
	const queryClient = useQueryClient();
	const clientSelectOptions = useClientSelectOptions();

	const schema = useMemo(() => {
		return yup.object({
			clientId: yup.string().required(),
			city: yup.string().default(""),
			countryId: yup.string().required(),
			dateOfBirth: yup.date().transform(transformEmptyDateValueToNull).nullable().default(null),
			emailBusiness: yup.string().email().default(""),
			emailPrivate: yup.string().email().default(""),
			firstName: yup.string().required(),
			genderId: yup.string().required(),
			houseNumber: yup.string().default(""),
			jobTitle: yup.string().default(""),
			languageId: yup.string().required(),
			lastName: yup.string().required(),
			managedBy: yup.string().required(),
			mobilePhone: yup.string().default(""),
			nobilityTitle: yup.string().default(""),
			phone: yup.string().default(""),
			salesFunnelId: yup.string().required(),
			salesTypeId: yup.string().required(),
			street: yup.string().default(""),
			title: yup.string().default(""),
			useInformalSalutation: yup.boolean().required(),
			zipCode: yup.string().default(""),
		});
	}, []);

	const defaultValues = useMemo(() => {
		return {
			clientId: contactPersonData.clientId,
			city: contactPersonData.city || "",
			countryId: contactPersonData.countryId,
			dateOfBirth: contactPersonData.dateOfBirth ? new Date(contactPersonData.dateOfBirth) : null,
			emailBusiness: contactPersonData.emailBusiness || "",
			emailPrivate: contactPersonData.emailPrivate || "",
			firstName: contactPersonData.firstName,
			genderId: contactPersonData.genderId,
			houseNumber: contactPersonData.houseNumber || "",
			jobTitle: contactPersonData.jobTitle || "",
			languageId: contactPersonData.languageId,
			lastName: contactPersonData.lastName,
			managedBy: contactPersonData.managedBy,
			mobilePhone: contactPersonData.mobilePhone || "",
			nobilityTitle: contactPersonData.nobilityTitle || "",
			phone: contactPersonData.phone || "",
			salesFunnelId: contactPersonData.salesFunnelId,
			salesTypeId: contactPersonData.salesTypeId,
			street: contactPersonData.street || "",
			title: contactPersonData.title || "",
			useInformalSalutation: contactPersonData.useInformalSalutation,
			zipCode: contactPersonData.zipCode || "",
		};
	}, [contactPersonData]);

	const {
		handleSubmit,
		control,
		formState: { errors, isDirty, isSubmitted, isValid },
		setValue,
		watch,
	} = useForm<UpdateClientContactPersonFormData>({
		mode: "onChange",
		defaultValues: defaultValues,
		resolver: yupResolver<UpdateClientContactPersonFormData>(schema),
	});

	const countrySelectOptions = useCountrySelectOptions();
	const genderSelectOptions = useGenderSelectOptions();
	const languageSelectOptions = useLanguageSelectOptions();
	const managerSelectOptions = useUserSelectOptions(userFilterOptions);
	const salesFunnelSelectOptions = useSalesFunnelSelectOptions();
	const salesTypeSelectOptions = useSalesTypeSelectOptions();

	const selectedClientId = watch("clientId");

	const formIsSubmittable = useFormIsSubmittable({
		isDirty,
		isLoading: busy,
		isValid,
		isSubmitted,
		enableInitially: false,
	});

	useEffect(() => {
		if (selectedClientId) {
			const selectedClientData = allClientsData?.find((client) => client.id === selectedClientId);
			if (selectedClientData) {
				setValue("street", selectedClientData.street);
				setValue("houseNumber", selectedClientData.houseNumber);
				setValue("zipCode", selectedClientData.zipCode);
				setValue("city", selectedClientData.city);
				setValue("countryId", selectedClientData.countryId);
			}
		}
	}, [selectedClientId, allClientsData, setValue]);

	const onSubmit: SubmitHandler<UpdateClientContactPersonFormData> = async (data) => {
		setBusy(true);
		try {
			const dateOfBirth = data.dateOfBirth ? formatDateToYYYYMMDD(new Date(data.dateOfBirth)) : null;
			const updateData: UpdateClientContactPersonData = { ...data, dateOfBirth };
			if (updateData.clientId !== contactPersonData.clientId) {
				updateData.newClientId = updateData.clientId;
			}
			updateData.dateOfBirth = data.dateOfBirth ? formatDateToYYYYMMDD(new Date(data.dateOfBirth)) : null;
			await updateClientContactPerson(contactPersonData.clientId, contactPersonData.id, updateData);
			await queryClient.invalidateQueries({ queryKey: CLIENT_CONTACT_PERSON_QUERY_KEY(contactPersonData.id) });
			queryClient.invalidateQueries({ queryKey: ALL_CLIENT_CONTACT_PERSONS_QUERY_KEY });
			onSuccess();
		} catch (error) {
			console.log(error);
		}
	};

	return (
		<form
			onSubmit={handleSubmit(onSubmit)}
			onKeyDown={preventSubmitOnEnter}
			className="flex min-h-full w-full flex-col justify-start"
		>
			<SidebarHeader>
				<SidebarHeaderHeadline>Kontaktdaten aktualisieren</SidebarHeaderHeadline>
			</SidebarHeader>
			<SidebarContent>
				{busy && <SidebarBusyOverlay />}
				<FormSectionGroup>
					<ContactCompanyFormSection<UpdateClientContactPersonFormData>
						control={control}
						clientSelectOptions={clientSelectOptions}
						errors={errors}
					/>
					<ContactPersonalInformationFormSection<UpdateClientContactPersonFormData>
						control={control}
						errors={errors}
						genderSelectOptions={genderSelectOptions}
					/>

					<ContactContactFormSection<UpdateClientContactPersonFormData>
						control={control}
					/>

					<AddressFormSection
						control={control}
						countrySelectOptions={countrySelectOptions}
					/>

					<ContactSalesFormSection
						control={control}
						errors={errors}
						languageSelectOptions={languageSelectOptions}
						managerSelectOptions={managerSelectOptions}
						salesFunnelSelectOptions={salesFunnelSelectOptions}
						salesTypeSelectOptions={salesTypeSelectOptions}
					/>
				</FormSectionGroup>
			</SidebarContent>
			<SidebarFooter>
				<FormHasErrorsHint show={!isValid}
								   className="mr-2" />
				<SubmitButton busy={busy}
							  disabled={!formIsSubmittable}>
					{t(normalizeTKey('form:buttonTexts.save'))}
				</SubmitButton>
				<Button theme="none"
						onClick={onCancel}>
					{t(normalizeTKey('form:buttonTexts.cancel'))}
				</Button>
			</SidebarFooter>
		</form>
	);
};

export default UpdateClientContactPersonForm;
